import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';

/**
 * The wait while the AI reads a pile of documents, kept light: one honest line about what is
 * happening, under it a status word that changes every couple of seconds (the way a build log
 * keeps you company). Nothing here is a progress bar: there is no progress to report.
 */

/** The default rotation for "reading the SOW and the notes". Callers may pass their own. */
export const GOALS_WAIT_LINES: readonly string[] = [
  'Reading the SOW cover to cover…',
  'Sorting objectives from asides…',
  'Discombobulating the jargon…',
  'Counting the systems you already have…',
  'Finding the pain points between the lines…',
  'Recombobulating…',
  'Guessing less, inferring more…',
  'Checking the timeline against the calendar…',
  'Polishing the draft…',
  'Nearly there. Promise.',
];

interface AiWaitingNoteProps {
  /** What is happening, in plain words: the first line. */
  title: string;
  /** What the person gets at the end, and that they can change it. */
  detail?: string;
  /** The rotating status lines; the last one repeats once reached. */
  lines?: readonly string[];
  /** Milliseconds between lines. */
  intervalMs?: number;
}

export function AiWaitingNote({ title, detail, lines = GOALS_WAIT_LINES, intervalMs = 2200 }: AiWaitingNoteProps) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    const timer = setInterval(() => setIndex((i) => (i + 1 < lines.length ? i + 1 : i)), intervalMs);
    return () => clearInterval(timer);
  }, [lines, intervalMs]);
  const line = lines[Math.min(index, lines.length - 1)] ?? '';
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="ai-waiting-note" role="status" aria-live="polite">
      <div className="relative">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" aria-hidden="true" />
        <Sparkles className="h-3.5 w-3.5 text-purple-400 absolute -top-1 -right-1" aria-hidden="true" />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        {detail && <p className="text-xs text-gray-500 mt-1">{detail}</p>}
        <p className="text-xs text-purple-700 mt-3 min-h-4 transition-opacity" data-testid="ai-waiting-line" key={line}>{line}</p>
      </div>
    </div>
  );
}
